import * as ProductService from "@omni/services/product/product.service";
import * as ComparisonProductTracker from "@omni/services/tracking/trackers/feature/comparisonProduct.tracker";
import { Product } from "@omni/types/api/product/bff/product.model";
import { ProductFieldSet } from "@omni/types/api/product/bff/shared/enums/productFieldSet.enum";
import { cn } from "@omni/utils/styles";
import { ChangeEvent, MouseEvent, useId } from "react";
import { useSelector } from "react-redux";

import { FormGroup } from "@/components/Form/Base/formGroup.component";
import { Input } from "@/components/Form/Base/input.component";
import { Label } from "@/components/Form/Base/label.component";
import { useThunkDispatch } from "@/redux/dispatcher";
import { set as setProductComparisonSkuList } from "@/redux/modules/value/productComparisonSkuList/productComparisonSkuList.module";
import * as ProductComparisonSkuListSelectors from "@/redux/modules/value/productComparisonSkuList/productComparisonSkuList.selectors";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { useIsClientSide } from "@/utils/hooks/isClient.hook";

type CompareProps = {
    product: Product<ProductFieldSet.Full | ProductFieldSet.ProductTile>;
    className?: string;
};

const Compare = ({ product, className }: CompareProps) => {
    const { t } = useGranite();
    const isClient = useIsClientSide();
    const dispatch = useThunkDispatch();

    const productComparisonSkuList =
        useSelector(ProductComparisonSkuListSelectors.getSkuList) || [];
    const productComparisonMaxReached = useSelector(
        ProductComparisonSkuListSelectors.isMaxReached
    );

    const maxProductsInComparisonReached =
        productComparisonMaxReached &&
        !productComparisonSkuList.includes(product.sku);

    const disabled = !isClient || maxProductsInComparisonReached;

    const id = useId();

    const isAdditionalService = ProductService.isAdditionalService(product);

    const onSetProductComparisonSkuListInternal = (skuList: string[]) => {
        dispatch(setProductComparisonSkuList(skuList));
    };

    const handleOnClick = (event: MouseEvent) => {
        event.stopPropagation();
    };

    const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
        const isChecked = (event.target as HTMLInputElement)?.checked;

        if (isChecked) {
            ComparisonProductTracker.trackProductsAdd([product]);

            onSetProductComparisonSkuListInternal([
                ...productComparisonSkuList,
                product.sku
            ]);
        } else {
            ComparisonProductTracker.trackProductsRemove([product]);

            onSetProductComparisonSkuListInternal(
                productComparisonSkuList.filter(
                    (pcSku) => pcSku !== product.sku
                )
            );
        }
    };

    if (isAdditionalService || product.boxArticle) {
        return null;
    }

    return (
        <FormGroup
            toggle={true}
            onClick={handleOnClick}
            className={cn(className)}
            disabled={disabled}
        >
            <Label
                htmlFor={id}
                className={cn("tw:cursor-pointer")}
            >
                <Input
                    id={id}
                    type={"checkbox"}
                    disabled={disabled}
                    checked={productComparisonSkuList.includes(product.sku)}
                    onChange={handleOnChange}
                />
                <span
                    className={cn("tw:select-none", {
                        "tw:text-muted-foreground": disabled
                    })}
                >
                    {t(
                        maxProductsInComparisonReached
                            ? "productComparison.maxAmountSelected"
                            : "productComparison.compareProduct"
                    )}
                </span>
            </Label>
        </FormGroup>
    );
};

export { Compare };
